import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Send } from 'lucide-react';

interface SentToProducerModalProps {
  onClose: () => void;
  onSubmit: (data: { purchaseCode: string; referenceSku: string }) => Promise<void>;
  productName: string;
  initialPurchaseCode?: string;
  initialReferenceSku?: string;
}

const SentToProducerModal: React.FC<SentToProducerModalProps> = ({ 
  onClose, 
  onSubmit,
  productName,
  initialPurchaseCode = '',
  initialReferenceSku = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    purchaseCode: initialPurchaseCode,
    referenceSku: initialReferenceSku
  });

  useEffect(() => {
    setFormData({
      purchaseCode: initialPurchaseCode,
      referenceSku: initialReferenceSku
    });
  }, [initialPurchaseCode, initialReferenceSku]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const purchaseCode = formData.purchaseCode.trim();
    const referenceSku = formData.referenceSku.trim();
    
    if (!purchaseCode) {
      return;
    }
    
    if (!referenceSku) {
      return;
    }

    try {
      setLoading(true);
      await onSubmit({ purchaseCode, referenceSku });
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
          <div>
            <CardTitle className="text-lg">Üreticiye Gönderildi</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{productName}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Satın Alma Kodu *
              </label>
              <input
                type="text"
                value={formData.purchaseCode}
                onChange={(e) => setFormData({...formData, purchaseCode: e.target.value})}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                placeholder="Satın alma kodunu giriniz"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Referans Kodu *
              </label>
              <input
                type="text"
                value={formData.referenceSku}
                onChange={(e) => setFormData({...formData, referenceSku: e.target.value})}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                placeholder="Referans kodunu giriniz"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading || !formData.purchaseCode.trim() || !formData.referenceSku.trim()}
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Kaydediliyor...' : 'Kaydet ve İlet'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SentToProducerModal;
